import { Directive, ElementRef, Input, AfterViewInit, OnDestroy, Renderer2, HostListener } from '@angular/core';

@Directive({
    selector: '[appDynamicHeight]',
    standalone: true
})
export class DynamicHeightDirective implements AfterViewInit, OnDestroy {
    /**
     * Offset from the bottom of the viewport in pixels.
     */
    @Input('appDynamicHeight') offsetBottom: number = 0;

    /**
     * Minimum height to maintain in pixels.
     */
    @Input() minHeight: number = 200;

    private resizeObserver: ResizeObserver | null = null;
    private mutationObserver: MutationObserver | null = null;

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    ngAfterViewInit(): void {
        this.allCalculations();

        // Optional: Observe parent size changes too
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => {
                requestAnimationFrame(() => this.calculateHeight());
            });
            this.resizeObserver.observe(document.body);
        }

        // Also observe for any DOM changes that might affect layout
        if (window.MutationObserver) {
            this.mutationObserver = new MutationObserver(() => {
                requestAnimationFrame(() => this.calculateHeight());
            });
            this.mutationObserver.observe(this.el.nativeElement.parentElement || document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.mutationObserver) this.mutationObserver.disconnect();
    }

    @HostListener('window:resize')
    onResize(): void {
        this.calculateHeight();
    }

    private allCalculations(): void {
        this.calculateHeight();
        // Run again after microtasks and multiple delays to catch layout settling
        requestAnimationFrame(() => this.calculateHeight());
        setTimeout(() => this.calculateHeight(), 100);
        setTimeout(() => this.calculateHeight(), 300);
    }

    private readonly RESPONSIVE_BREAKPOINT = 1024;

    private calculateHeight(): void {
        if (!this.el.nativeElement) return;

        // Skip fixed height on responsive layouts — let content flow naturally
        if (window.innerWidth <= this.RESPONSIVE_BREAKPOINT) {
            this.renderer.removeStyle(this.el.nativeElement, 'height');
            return;
        }

        const windowHeight = window.innerHeight;
        const rect = this.el.nativeElement.getBoundingClientRect();

        // Only calculate if the element is somewhat visible/active
        if (rect.top === 0 && rect.height === 0) return;

        // Calculate the remaining height
        let calculatedHeight = windowHeight - rect.top - this.offsetBottom;

        // Apply minimum height constraint
        if (calculatedHeight < this.minHeight) {
            calculatedHeight = this.minHeight;
        }

        this.renderer.setStyle(this.el.nativeElement, 'height', `${calculatedHeight}px`);
    }
}
